import React, { useEffect, useMemo, useState } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Row,
  Col,
} from "reactstrap"
import Flatpickr from "react-flatpickr"
import "flatpickr/dist/themes/material_blue.css"
import { useTranslation } from "react-i18next"
import { X } from "lucide-react"
import { useSelector, useDispatch } from "react-redux"
import { QrReader } from "react-qr-reader"
import { Field, useFormik } from "formik"
import * as Yup from "yup"

import CustomSingleSelect from "components/Shared/CustomSingleSelect"
import CustomLabel from "components/Common/CustomLabel"
import {
  getTrialPeriodSchedule,
  markTrialOperationInspectionAttendance,
  markTrialPeriodAttendance,
} from "store/actions/trialPeriodAction"
import { formatDateForAPI } from "components/Common/Utils"
import { showAlert } from "store/reducers/alertReducer"
import { isSameDay } from "date-fns"

const ErrorMessage = ({ name, errors, touched }) => {
  if (!errors[name] || !touched[name]) return null

  return <div className="text-danger fw-semibold mt-1">{errors[name]}</div>
}

const InspectionAttendanceModal = ({
  isOpen,
  toggle,
  trial_run_schedule_uuid,
  attendanceDetails,
  trialOperationDetails,
}) => {
  const [hovered, setHovered] = useState(null)
  const [nationalIds, setNationalIds] = useState("")
  const [payload, setPayload] = useState({
    trial_run_schedule_uuid: null,
    national_ids: "",
    type: null,
    date: new Date(),
  })
  const [trialPeriodSchedules, setTrialPeriodSchedules] = useState([])
  const [attendanceType, setAttendanceType] = useState("MANUAL")
  const [idEntities, setIdEntities] = useState([])
  const [chunked, setChunked] = useState([])

  const {
    t,
    i18n: { language },
  } = useTranslation()

  const dispatch = useDispatch()

  const { work_shifts, stations } = useSelector(state => state.Common)
  const getShiftKey = shiftName => {
    return (
      Object.keys(work_shifts).find(
        key => work_shifts[key].name === shiftName
      ) || null
    )
  }

  const getStationKey = stationName => {
    return (
      Object.keys(stations).find(key => stations[key].name === stationName) ||
      null
    )
  }
  const initialValues = {
    shift: attendanceDetails
      ? getShiftKey(attendanceDetails?.shift)
      : trialOperationDetails
      ? trialOperationDetails?.shift
      : null,
    station: attendanceDetails
      ? getStationKey(attendanceDetails?.station)
      : trialOperationDetails
      ? trialOperationDetails?.station.toString()
      : null,
    type: null,
    reason: null,
    remark: null,
  }
  const [selectedButton, setSelectedButton] = useState({
    shift: null,
    station: null,
  })

  useEffect(() => {
    if (attendanceDetails) {
      setSelectedButton({
        shift: getShiftKey(attendanceDetails?.shift),
        station: getStationKey(attendanceDetails?.station),
      })
      setIdEntities([attendanceDetails?.user?.national_id])
      setChunked([attendanceDetails?.user?.national_id])
    }
  }, [attendanceDetails])

  useEffect(() => {
    if (trialOperationDetails) {
      setSelectedButton({
        shift: trialOperationDetails?.shift,
        station: trialOperationDetails?.station.toString(),
      })
    }
  }, [trialOperationDetails])

  const attendanceTypes = { present: "Present", absent: "Absent" }

  const validationSchema = Yup.object().shape({
    shift: Yup.string().required(t("Field is required")),
    station: Yup.string().required(t("Field is required")),
    type: Yup.string().required(t("Field is required")),
    remark: Yup.string().when("type", {
      is: "absent",
      then: schema => schema.required(t("Field is required")),
      otherwise: schema => schema.notRequired(),
    }),
  })
  const buttonStyle = (selected, text, isHovered) => {
    return {
      flex: "1 1 100px",
      margin: "4px",
      padding: "10px 15px",
      border: `1px solid  ${
        selected === text || isHovered
          ? "#00778a"
          : text === "present"
          ? "green"
          : text === "absent"
          ? "red"
          : "#99c1cd"
      }`,
      background: selected === text || isHovered ? "#00778a" : "white",
      color:
        selected === text || isHovered
          ? "white"
          : text === "absent"
          ? "red"
          : text === "present"
          ? "green"
          : "#5d98a0",
      fontSize: "14px",
      cursor: "pointer",
      fontWeight: selected === text ? "bold" : "normal",
      transition: "all 0.3s ease",
      borderRadius: "5px",
      minWidth: "120px",
    }
  }

  const onMarkAttendance = values => {
    const { type, reason, shift, station, remark } = values

    if (attendanceType === "MANUAL" && idEntities.length === 0) {
      return dispatch(
        showAlert({ text: t("Please enter National IDs"), type: "error" })
      )
    }

    const ids = chunked || idEntities
    const obj = {
      reason: reason ? reason.value : "Random Check",
      type: type === "present" ? 1 : 2,
      date: formatDateForAPI(payload.date),
      national_ids: ids,
      shift,
      station,
      trial_run_schedule_uuid,
      remark,
    }
    dispatch(markTrialOperationInspectionAttendance(obj))
      .then(action => {
        if (!action.payload) {
          return dispatch(
            showAlert({
              text: t("The selected national_ids.0 is invalid."),
              type: "error",
            })
          )
        }

        const { statusCode, success, message, data } = action?.payload

        if (statusCode === 200 && success && (!data || data.length === 0)) {
          dispatch(showAlert({ text: message, type: "success" }))
          setIdEntities([])
          toggle()
        } else {
          if (data && data.length > 0) {
            const nationalIds = data.map(d => d.national_id)
            setIdEntities(nationalIds)
            dispatch(showAlert({ text: data[0].message, type: "error" }))
          } else {
            dispatch(showAlert({ text: message, type: "error" }))
          }
        }
      })
      .catch(err => {
        console.log("err", err)
      })
  }

  const {
    values,
    errors,
    touched,
    setFieldValue,
    handleSubmit,
    handleBlur,
    isValid,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: onMarkAttendance,
  })

  useEffect(() => {
    if (payload.date && selectedButton.shift && selectedButton.station) {
      getTrailSchedule()
    }
  }, [selectedButton, payload.date])

  const getTrailSchedule = () => {
    let obj = {
      ...selectedButton,
      date: formatDateForAPI(payload.date),
    }
    dispatch(getTrialPeriodSchedule(obj))
      .then(action => {
        const { statusCode, success, message, data } = action?.payload
        if (statusCode === 200 && success) {
          setTrialPeriodSchedules(data)
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(() => {})
  }

  const formattedTrialPeriodSchedules = useMemo(() => {
    if (!trialPeriodSchedules) return []
    const updatedTrialPeriodSchedules = trialPeriodSchedules.map(item => ({
      label: item.title,
      value: item.uuid,
    }))
    setPayload({
      ...payload,
      trial_run_schedule_uuid: updatedTrialPeriodSchedules[0]?.value,
    })
    setFieldValue(
      "trial_run_schedule_uuid",
      updatedTrialPeriodSchedules[0]?.value
    )
    return updatedTrialPeriodSchedules
  }, [trialPeriodSchedules])

  const removeId = idToRemove => {
    const updatedEntities = idEntities.filter(id => id !== idToRemove)
    setIdEntities(updatedEntities)
    setNationalIds(updatedEntities.join(""))
  }

  const handleInputChange = e => {
    const input = e.target.value.replace(/[^0-9]/g, "")
    setNationalIds(input)
    const patch = input.match(/\d{10}/g) || []
    if (patch.length > 0) {
      setIdEntities(prev => Array.from(new Set([...prev, ...patch])))
      setChunked(patch)
      setNationalIds("")
      if (attendanceType !== "MANUAL") {
        handleSubmit()
      }
    }
  }

  const handleQRScan = (result, error) => {
    if (result) {
      const matchedIds = result.getText().match(/\d{10}/g)
      handleInputChange({ target: { value: matchedIds?.join("") } })
    }
  }

  return (
    <Modal size="lg" isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>{t("Capture Random Check")}</ModalHeader>
      <ModalBody>
        <Row>
          <Col xs={12} className="mb-2">
            <CustomLabel label={t("Shift")} isRequired />
            <div className="d-flex flex-wrap">
              {Object.keys(work_shifts).map(option => (
                <button
                  key={option}
                  name="shift"
                  onMouseEnter={() => setHovered(option)}
                  onMouseLeave={() => setHovered(null)}
                  onBlur={handleBlur}
                  style={buttonStyle(
                    selectedButton.shift,
                    option,
                    hovered === option
                  )}
                  onClick={() => {
                    setSelectedButton({ ...selectedButton, shift: option })
                    setFieldValue("shift", option)
                  }}
                  disabled={attendanceDetails?.shift  || trialOperationDetails?.shift}
                >
                  {t(
                    work_shifts[option][language === "en" ? "name" : "name_ar"]
                  )}
                </button>
              ))}
            </div>
            <ErrorMessage errors={errors} touched={touched} name="shift" />
          </Col>

          <Col xs={12} className="mb-2">
            <CustomLabel label={t("Select the station")} isRequired />
            <div className="d-flex flex-wrap">
              {Object.keys(stations).map(option => (
                <button
                  key={option}
                  name="station"
                  onBlur={handleBlur}
                  onMouseEnter={() => setHovered(option)}
                  onMouseLeave={() => setHovered(null)}
                  style={buttonStyle(
                    selectedButton.station,
                    option,
                    hovered === option
                  )}
                  onClick={() => {
                    setSelectedButton({ ...selectedButton, station: option })
                    setFieldValue("station", option)
                  }}
                  disabled={attendanceDetails?.station || trialOperationDetails?.station}
                >
                  {t(stations[option][language === "en" ? "name" : "name_ar"])}
                </button>
              ))}
            </div>
            <ErrorMessage errors={errors} touched={touched} name="station" />
          </Col>

          <Col xs="12" sm="6" className="mb-1">
            <FormGroup>
              <CustomLabel label={t("Select Date")} isRequired />
              <Flatpickr
                options={{ dateFormat: "d-m-Y" }}
                value={payload.date ? new Date(payload.date) : null}
                disabled
                onChange={selectedDates =>
                  setPayload({ ...payload, date: selectedDates[0] })
                }
                className="form-control"
              />
            </FormGroup>
          </Col>

          {/*<Col md={6} xs="12" className="mb-3">*/}
          {/*  <CustomLabel label={t("Reason")} isRequired={false} />*/}
          {/*  <CustomSingleSelect*/}
          {/*    value={values.reason}*/}
          {/*    options={[*/}
          {/*      {*/}
          {/*        label: t("Different Station"),*/}
          {/*        value: t("Differnet Station"),*/}
          {/*      },*/}
          {/*    ]}*/}
          {/*    onChange={e => {*/}
          {/*      setFieldValue("reason", e)*/}
          {/*    }}*/}
          {/*    placeholder={t("Select reason")}*/}
          {/*    name="reason"*/}
          {/*    onBlur={handleBlur}*/}
          {/*  />*/}
          {/*</Col>*/}

          <Col xs="12" className="mb-3">
            <CustomLabel label={t("Attendance Type")} isRequired />
            <div className="d-flex flex-wrap">
              {Object.keys(attendanceTypes).map(option => (
                <button
                  key={option}
                  name="type"
                  onBlur={handleBlur}
                  onMouseEnter={() => setHovered(option)}
                  onMouseLeave={() => setHovered(null)}
                  style={buttonStyle(payload.type, option, hovered === option)}
                  onClick={() => {
                    setPayload({ ...payload, type: option })
                    setFieldValue("type", option)
                  }}
                >
                  {t(attendanceTypes[option])}
                </button>
              ))}
            </div>
            <ErrorMessage errors={errors} touched={touched} name="type" />
          </Col>

          {values.type === "absent" && (
            <Col lg={12}>
              <div className="form_field">
                <CustomLabel
                  htmlFor="remarks"
                  label={t("Remarks")}
                  isRequired
                />
                {/*<Field*/}
                {/*  as="textarea"*/}
                {/*  name="remarks"*/}
                {/*  className="form-control"*/}
                {/*  placeholder={t("Enter Remarks")}*/}
                {/*  maxLength={250}*/}
                {/*></Field>*/}

                {/*<Col xs="12" className="mb-3">*/}
                {/*  <CustomLabel label={t("Remarks")} isRequired />*/}
                <textarea
                  className="form-control"
                  value={values.remark}
                  onChange={e => {
                    setFieldValue("remark", e.target.value)
                  }}
                  placeholder={t("Enter Remarks")}
                  maxLength={2000}
                />
                {/*</Col>*/}
              </div>
              {/*<ErrorMessage*/}
              {/*  name={"remark"}*/}
              {/*  component="div"*/}
              {/*  className="text-danger"*/}
              {/*/>*/}
            </Col>
          )}

          {attendanceDetails === undefined && (
            <Col xs="12" className="mb-3">
              <div className="d-flex flex-wrap align-items-center gap-3">
                <label className="d-flex align-items-center">
                  <input
                    type="radio"
                    name="uploadType"
                    value="MANUAL"
                    checked={attendanceType === "MANUAL"}
                    onClick={() => setAttendanceType("MANUAL")}
                    className="ms-2 me-2"
                  />
                  {t("Manual")}
                </label>
                <label className="d-flex align-items-center">
                  <input
                    type="radio"
                    name="uploadType"
                    value="QR"
                    checked={attendanceType === "QR"}
                    onClick={() => {
                      setAttendanceType("QR")
                      setIdEntities([])
                      setNationalIds("")
                    }}
                    className="ms-2 me-2"
                  />
                  {t("QR Code")}
                </label>
              </div>
            </Col>
          )}

          {idEntities.length > 0 && (
            <Col xs="12" className="mb-3">
              <div className="border rounded p-3">
                <div className="d-flex flex-wrap gap-2">
                  {idEntities.map((id, index) => (
                    <span
                      key={index}
                      className="badge bg-primary d-flex align-items-center gap-2"
                      style={{ fontSize: "14px", padding: "6px 10px" }}
                    >
                      {id}
                      <X
                        size={16}
                        style={{ cursor: "pointer" }}
                        onClick={() => removeId(id)}
                      />
                    </span>
                  ))}
                </div>
              </div>
            </Col>
          )}

          {attendanceType === "QR" && (
            <Col xs="12" className="mb-3">
              <CustomLabel label={t("Scan QR Code")} />
              <div className="border rounded overflow-hidden p-0">
                <QrReader
                  constraints={{ facingMode: { ideal: "environment" } }}
                  onResult={handleQRScan}
                />
              </div>
            </Col>
          )}

          {attendanceType === "MANUAL" && (
            <Col xs="12" className="mb-3">
              <CustomLabel label={t("National ID / Iqama ID")} isRequired />
              <textarea
                className="form-control"
                value={nationalIds}
                onChange={handleInputChange}
                placeholder={t(
                  "Please enter national Id/ Iqama Id: eg. 1234657890, 2345678910"
                )}
                maxLength={2000}
                disabled={attendanceDetails}
              />
            </Col>
          )}
        </Row>
      </ModalBody>
      <ModalFooter>
        <div className="d-flex flex-wrap gap-2">
          <button
            type="button"
            className="main_btn disabled-button fit_content px-4"
            onClick={toggle}
          >
            {t("Cancel")}
          </button>
          {attendanceType === "MANUAL" && (
            <button
              type="button"
              className="main_btn fit_content px-4"
              onClick={() => handleSubmit()}
              disabled={!isValid}
            >
              {t("Save")}
            </button>
          )}
        </div>
      </ModalFooter>
    </Modal>
  )
}

export default InspectionAttendanceModal
